import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Field,
  Flex,
  HStack,
  Heading,
  Icon,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { LuLandmark, LuTriangleAlert } from 'react-icons/lu';
import { validateSetupToken, redeemSetupToken } from '@/lib/api';
import { toaster } from '@/components/ui/toaster';

type Status = 'validating' | 'valid' | 'invalid';

export function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('validating');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setStatus('invalid');
      return;
    }
    validateSetupToken(token)
      .then((r) => {
        if (active) setStatus(r.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        if (active) setStatus('invalid');
      });
    return () => {
      active = false;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('La contraseña debe tener al menos 12 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      await redeemSetupToken(token, password);
      toaster.create({
        title: 'Contraseña establecida',
        description: 'Ya puedes iniciar sesión.',
        type: 'success',
      });
      navigate('/login', { replace: true });
    } catch (err) {
      // A consumed/expired token surfaces here — flip to the invalid state.
      setError(err instanceof Error ? err.message : 'No se pudo establecer la contraseña.');
      setStatus('invalid');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Flex minH="100dvh" bg="bg.canvas" align="center" justify="center" p={{ base: '5', md: '10' }}>
      <Container maxW="md">
        <Stack gap="8">
          <HStack gap="3">
            <Flex
              w="11"
              h="11"
              align="center"
              justify="center"
              rounded="lg"
              bg="brand.600"
              color="white"
              shadow="sm"
            >
              <Icon as={LuLandmark} boxSize="6" />
            </Flex>
            <Text fontWeight="800" fontSize="md" color="fg">
              CrediFacilGT
            </Text>
          </HStack>

          {status === 'validating' && (
            <HStack gap="3" color="fg.muted">
              <Spinner size="sm" />
              <Text fontSize="sm">Validando enlace…</Text>
            </HStack>
          )}

          {status === 'invalid' && (
            <Stack gap="4">
              <HStack gap="3" color="red.600">
                <Icon as={LuTriangleAlert} boxSize="6" />
                <Heading size={{ base: 'lg', md: 'xl' }} letterSpacing="-0.02em">
                  Enlace inválido o vencido
                </Heading>
              </HStack>
              <Text color="fg.muted" fontSize="sm">
                Este enlace ya se usó, venció o no es válido. Solicita uno nuevo a tu administrador.
              </Text>
              <Box>
                <Button asChild variant="outline">
                  <RouterLink to="/login">Ir a iniciar sesión</RouterLink>
                </Button>
              </Box>
            </Stack>
          )}

          {status === 'valid' && (
            <Stack gap="6">
              <Stack gap="2">
                <Text
                  fontSize="sm"
                  color="fg.muted"
                  letterSpacing="0.05em"
                  textTransform="uppercase"
                >
                  Acceso interno
                </Text>
                <Heading size={{ base: 'lg', md: 'xl' }} letterSpacing="-0.02em">
                  Establecer contraseña
                </Heading>
                <Text color="fg.muted" fontSize="sm">
                  Elige una contraseña de al menos 12 caracteres.
                </Text>
              </Stack>

              <form onSubmit={onSubmit}>
                <Stack gap="4">
                  {/* Plain Field.Root (no `required` on the Root) so the field's
                      accessible name stays exactly "Contraseña" — the e2e selects
                      it with getByLabel(..., { exact: true }). Matches Login.tsx. */}
                  <Field.Root>
                    <Field.Label>Contraseña</Field.Label>
                    <Input
                      size="lg"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      minLength={12}
                      required
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Confirmar contraseña</Field.Label>
                    <Input
                      size="lg"
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.currentTarget.value)}
                      minLength={12}
                      required
                    />
                  </Field.Root>
                  {error && (
                    <Text color="red.600" fontSize="sm">
                      {error}
                    </Text>
                  )}
                  <Button
                    type="submit"
                    size="lg"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                    loading={submitting}
                    loadingText="Guardando…"
                  >
                    Establecer contraseña
                  </Button>
                </Stack>
              </form>
            </Stack>
          )}
        </Stack>
      </Container>
    </Flex>
  );
}
